import { isResource, resolveId } from '@medplum/core'
import type {
  Appointment,
  AppointmentParticipant,
  CodeableConcept,
  Coding,
  Extension as FHIRExtension,
  HealthcareService,
  Location,
  Patient,
  Practitioner,
  Reference,
  RelatedPerson,
  ServiceRequest,
} from '@medplum/fhirtypes'
import { endOfDay, isPast, isToday, startOfDay } from 'date-fns'
import { castArray, compact, uniqBy } from 'lodash-es'
import {
  codeableConcept,
  codeFromCodeableConcept,
} from '#src/codeableConcept.ts'
import type { AppointmentOrigin, AppointmentType } from '#src/codeSystems.ts'
import * as codeSystems from '#src/codeSystems.ts'
import { endOfWeek, startOfWeek, toDateString } from '#src/dates.ts'
import { Extension, Tag as TagDecorator } from '#src/decorators.ts'
import { base32hexEncode } from '#src/encoding.ts'
import { ExtendedResource } from '#src/ExtendedResource.ts'
import { invariant } from '#src/invariant.ts'
import { merge } from '#src/merge/merge.ts'
import { isProfileResource } from '#src/models/profile.ts'
import { getReferenceString } from '#src/references.ts'
import type { QueryTypes } from '#src/search.ts'
import {
  APPOINTMENT_NOTES,
  APPOINTMENT_RECURRENCE_INSTANCE_LINK,
} from '#src/structureDefinitions.ts'
import { isOfType } from '#src/utils.ts'

export const upcomingOrDraftAppointmentStatuses: Appointment['status'][] = [
  'proposed',
  'pending',
  'booked',
] as const

// _tag is a FHIR token search: a comma in the (decoded) value is the spec's
// OR-separator, so an unvalidated batchId containing one would widen a
// schedule-proposal search to every token it lists (e.g. every Seen-origin
// appointment via the AppointmentOrigin tag). Restrict to the charset
// stage_schedule_proposal actually generates/accepts so no token-syntax
// character (comma, pipe, whitespace) can reach the query.
function assertValidBatchId(batchId: string): string {
  if (!/^[A-Za-z0-9_-]+$/.test(batchId)) {
    throw new Error(`Invalid schedule-proposal batchId: ${batchId}`)
  }
  return batchId
}

export class ExtendedAppointment extends ExtendedResource<Appointment> {
  static readonly RESOURCE_TYPE = 'Appointment'

  static isInstance(value: unknown): value is Appointment {
    return isResource(value) && isOfType(value, 'Appointment')
  }

  // `patient` accepts a list so one search covers many participants;
  // `normalizeQuery` comma-joins it into a reference-OR. Page the result
  // (`searchResourceClassPages*`) when passing more than a handful — `_count`
  // bounds a page, not the match set.
  static byStatuses({
    statuses,
    patient,
    startsOnOrAfter,
  }: {
    statuses: Appointment['status'][]
    patient: Reference<Patient> | Reference<Patient>[]
    startsOnOrAfter?: string
  }): QueryTypes {
    const query: QueryTypes = { patient, status: statuses }
    if (startsOnOrAfter) {
      query.date = `ge${startsOnOrAfter}`
    }
    query._sort = '-_lastUpdated'
    query._count = '1000'
    return query
  }

  static byBasedOn(basedOn: Reference<ServiceRequest>): QueryTypes {
    return {
      'based-on': basedOn,
      _sort: '-_lastUpdated',
      _count: '1000',
    }
  }

  static byBasedOnList(basedOn: Reference<ServiceRequest>[]): QueryTypes {
    return {
      'based-on': basedOn,
      _sort: '-_lastUpdated',
      _count: '1000',
    }
  }

  static bookedByDayWithActors(
    date: string,
    actors: Reference<Patient | Practitioner | RelatedPerson>[]
  ): QueryTypes {
    const start = startOfDay(date).toISOString()
    const end = endOfDay(date).toISOString()

    const actorsFilter = actors.map((a) => `actor re ${getReferenceString(a)}`)

    const _filter = compact([
      `date gt ${start}`,
      `date lt ${end}`,
      ...actorsFilter,
    ]).join(' and ')

    return {
      status: ['booked'],
      actor: actors,
      _tag: codeSystems.AppointmentOrigin.Seen,
      _filter,
    }
  }

  static bookedByServiceInWeek({
    patient,
    practitioner,
    healthcareService,
    start,
    end,
  }: {
    patient: Reference<Patient>
    practitioner: Reference<Practitioner>
    healthcareService: Reference<HealthcareService>
    start: string
    end: string
  }): QueryTypes {
    const weekStart = startOfWeek(start).toISOString()
    const weekEnd = endOfWeek(end).toISOString()
    return {
      patient,
      practitioner,
      actor: healthcareService,
      _filter: `date gt ${weekStart} and date lt ${weekEnd}`,
      _tag: codeSystems.AppointmentOrigin.Seen,
    }
  }

  static byScheduleProposalBatch(batchId: string): QueryTypes {
    return {
      _tag: `${codeSystems.SCHEDULE_PROPOSAL_SYSTEM}|${assertValidBatchId(batchId)}`,
      _sort: 'date',
      _count: '1000',
    }
  }

  // Dedupe key for conditional create: same patient+practitioner+service+week
  // within this batch. Matches `proposed` AND `booked` rows — a partial
  // commit can leave some batch entries `booked` while others stay
  // `proposed`; re-staging the same logical batch must see the `booked` ones
  // too, or it creates duplicate `proposed` rows for slots already booked,
  // which a later commit would then double-book.
  static proposedByServiceInWeek({
    patient,
    practitioner,
    healthcareService,
    start,
    batchId,
  }: {
    patient: Reference<Patient>
    practitioner: Reference<Practitioner>
    healthcareService: Reference<HealthcareService>
    start: string
    batchId: string
  }): QueryTypes {
    const weekStart = startOfWeek(start).toISOString()
    const weekEnd = endOfWeek(start).toISOString()
    return {
      patient,
      practitioner,
      actor: healthcareService,
      status: ['proposed', 'booked'],
      _filter: `date gt ${weekStart} and date lt ${weekEnd}`,
      _tag: `${codeSystems.SCHEDULE_PROPOSAL_SYSTEM}|${assertValidBatchId(batchId)}`,
    }
  }

  // Any appointment touching one of these patients/practitioners within the
  // window, regardless of which of them it is for (OR semantics — mirrors
  // bookedByDayWithActors's plain `actor` array field, not its `_filter`-based
  // exact-match variant).
  static byActorsInWindow({
    actors,
    window,
    statuses,
  }: {
    actors: Reference<Patient | Practitioner>[]
    window: { start: string; end: string }
    statuses: Appointment['status'][]
  }): QueryTypes {
    return {
      actor: actors,
      status: statuses,
      _filter: `date ge ${window.start} and date le ${window.end}`,
      _count: '1000',
    }
  }

  get shouldShowCheckIn(): boolean {
    if (this.isPractitionerBlock) {
      return false
    }
    return (
      (!this.isCancelled && !!this.start && isToday(this.start)) ||
      this.isSpecialistVisit
    )
  }

  get shouldShowStartVisitNote(): boolean {
    if (this.isPractitionerBlock) {
      return false
    }
    return !this.isCancelled && !!this.start && isToday(this.start)
  }

  get appointmentType(): AppointmentType | undefined {
    return codeFromCodeableConcept(
      this.resource.appointmentType,
      codeSystems.APPOINTMENT_TYPE
    ) as AppointmentType | undefined
  }

  get isPractitionerBlock(): boolean {
    return codeSystems.isPractitionerBlockType(this.appointmentType)
  }

  get practitionerBlockDisplay(): string | undefined {
    const type = this.appointmentType
    return codeSystems.isPractitionerBlockType(type)
      ? codeSystems.PRACTITIONER_BLOCKS[type].display
      : undefined
  }

  get start(): string | undefined {
    return this.resource.start
  }

  set start(value: string | undefined) {
    this.resource.start = value
  }

  get startDateOnly(): string | undefined {
    return this.start ? toDateString(this.start) : undefined
  }

  get end(): string | undefined {
    return this.resource.end
  }

  set end(value: string | undefined) {
    this.resource.end = value
  }

  get endDateOnly(): string | undefined {
    return this.end ? toDateString(this.end) : undefined
  }

  get reason(): string | undefined {
    return (
      this.resource.reasonCode?.[0]?.text ??
      this.healthcareServices?.[0]?.display ??
      this.resource.serviceType?.[0]?.text
    )
  }

  get basedOn(): Reference<ServiceRequest>[] | undefined {
    return this.resource.basedOn
  }

  set basedOn(value: Reference<ServiceRequest>[] | undefined) {
    this.resource.basedOn = value
  }

  addBasedOn(value: Reference<ServiceRequest>): void {
    const next = (this.basedOn ?? []).filter(
      (b) => getReferenceString(b) !== getReferenceString(value)
    )
    this.resource.basedOn = compact([...next, value])
  }

  removeBasedOn(value: Reference<ServiceRequest>): void {
    const next = (this.basedOn ?? []).filter(
      (b) => getReferenceString(b) !== getReferenceString(value)
    )
    this.resource.basedOn = next
  }

  get status(): Appointment['status'] {
    return this.resource.status
  }

  set status(value: Appointment['status']) {
    this.resource.status = value
  }

  set supportingInformation(value: Reference[] | undefined) {
    this.resource.supportingInformation = value
  }

  get supportingInformation(): Reference[] | undefined {
    return this.resource.supportingInformation
  }

  get participantReferences(): NonNullable<AppointmentParticipant['actor']>[] {
    return this.resource.participant.map((p) => p.actor).filter((v) => !!v)
  }

  get participantReferenceStrings(): string[] {
    return this.participantReferences.map(getReferenceString)
  }

  get personParticipants(): NonNullable<
    Reference<Patient | Practitioner | RelatedPerson>
  >[] {
    return this.participantReferences.filter((p) => isProfileResource(p))
  }

  get patients(): Reference<Patient>[] {
    return this.participantReferences.filter((p) => isOfType(p, 'Patient'))
  }

  set patient(value: Reference<Patient> | undefined) {
    const participants = this.resource.participant.filter(
      (p) => !isOfType(p.actor, 'Patient')
    )
    this.resource.participant = compact([
      ...participants,
      { actor: value, status: 'accepted' as const },
    ])
  }

  get patient(): Reference<Patient> | undefined {
    return this.patients?.[0]
  }

  get healthcareServices(): Reference<HealthcareService>[] {
    return this.participantReferences.filter((p) =>
      isOfType(p, 'HealthcareService')
    )
  }

  get healthcareService(): Reference<HealthcareService> | undefined {
    return this.healthcareServices?.[0]
  }

  set healthcareService(value: Reference<HealthcareService> | undefined) {
    const participants = this.resource.participant.filter(
      (p) => !isOfType(p.actor, 'HealthcareService')
    )
    this.resource.participant = compact([
      ...participants,
      value && { actor: value, status: 'accepted' as const },
    ])
  }

  // The specialty a clinical-specialist visit is for. The generic
  // "specialist visit" HealthcareService is shared across every external
  // specialty, so this is what distinguishes a cardiology booking from an
  // endocrinology one — and what lets a later booking find the right prior
  // visit without inferring it from whoever happened to staff it.
  get specialties(): CodeableConcept[] {
    return this.resource.specialty ?? []
  }

  get specialty(): CodeableConcept | undefined {
    return this.specialties[0]
  }

  set specialty(value: CodeableConcept | undefined) {
    this.resource.specialty = value ? [value] : undefined
  }

  get practitioners(): Reference<Practitioner>[] {
    return this.participantReferences.filter((p) => isOfType(p, 'Practitioner'))
  }

  set practitioners(
    value: Reference<Practitioner> | Reference<Practitioner>[] | undefined
  ) {
    const practitioners = value ? castArray(value) : []
    this.resource.participant = compact([
      ...this.resource.participant.filter(
        (p) => !isOfType(p.actor, 'Practitioner')
      ),
      ...practitioners.map(
        (p) =>
          ({
            actor: p,
            status: 'accepted',
          }) satisfies AppointmentParticipant
      ),
    ])
  }

  // Swaps one clinician for another, keeping the rest. `from` absent assigns a
  // visit that had none. Returns false when `from` is no longer on the visit,
  // so a caller acting on a stale copy can refuse instead of overwriting.
  replacePractitioner(
    from: string | undefined,
    to: Reference<Practitioner>
  ): boolean {
    const current = this.practitioners
    if (from && !current.some((p) => p.reference === from)) {
      return false
    }
    const rest = current.filter((p) => p.reference !== from)
    this.practitioners = rest.some((p) => p.reference === to.reference)
      ? rest
      : [...rest, to]
    return true
  }

  get locations(): Reference<Location>[] {
    return this.participantReferences.filter((p) => isOfType(p, 'Location'))
  }

  get location(): Reference<Location> | undefined {
    // Find first location that is NOT a pickup location
    const participant = this.resource.participant.find(
      (p) =>
        isOfType(p.actor, 'Location') &&
        !ExtendedAppointment.isPickupLocation(p)
    )
    return participant?.actor as Reference<Location> | undefined
  }

  set location(value: Reference<Location> | undefined) {
    const participants = this.resource.participant.filter(
      (p) =>
        !isOfType(p.actor, 'Location') ||
        ExtendedAppointment.isPickupLocation(p)
    )
    this.resource.participant = compact([
      ...participants,
      value && { actor: value, status: 'accepted' as const },
    ])
  }

  get locationDisplay(): string | undefined {
    return this.location?.display
  }

  updateLocation(location: Reference<Location>): void {
    this.resource.participant = this.resource.participant.filter(
      (p) =>
        !isOfType(p.actor, 'Location') ||
        ExtendedAppointment.isPickupLocation(p)
    )
    this.resource.participant.push({
      actor: location,
      status: 'accepted',
    })
  }

  private static isPickupLocation(p: AppointmentParticipant): boolean {
    return (
      isOfType(p.actor, 'Location') &&
      !!p.type?.some((t) =>
        t.coding?.some(
          (c) =>
            c.system === codeSystems.DELIVERY_LOCATION_TYPE &&
            c.code === codeSystems.DeliveryLocationType.Pickup
        )
      )
    )
  }

  get pickupLocation(): Reference<Location> | undefined {
    const participant = this.resource.participant.find((p) =>
      ExtendedAppointment.isPickupLocation(p)
    )
    return participant?.actor as Reference<Location> | undefined
  }

  set pickupLocation(value: Reference<Location> | undefined) {
    this.resource.participant = this.resource.participant.filter(
      (p) => !ExtendedAppointment.isPickupLocation(p)
    )
    if (value) {
      this.resource.participant.push({
        actor: value,
        type: [
          {
            coding: [
              {
                system: codeSystems.DELIVERY_LOCATION_TYPE,
                code: codeSystems.DeliveryLocationType.Pickup,
                display: 'Pickup Location',
              },
            ],
          },
        ],
        status: 'accepted',
      })
    }
  }

  // For delivery appointments, dropoff location is the same as the regular location
  get dropoffLocation(): Reference<Location> | undefined {
    return this.location
  }

  set dropoffLocation(value: Reference<Location> | undefined) {
    this.location = value
  }

  get isPast(): boolean {
    const time = this.resource.end ?? this.resource.start
    return !!time && isPast(time)
  }

  // entered-in-error is a row that should not have been created. The drawer,
  // check-in, and practitioner calendar treat it like a cancellation.
  get isCancelled(): boolean {
    return (
      this.resource.status === 'cancelled' ||
      this.resource.status === 'entered-in-error'
    )
  }

  // The following checks are brittle and subject to breakage if the healthcare service names change.
  // Our models (ExtendedAppointment) do not hydrate related resources (healthcare services, locations, etc.)
  // so we can't use the related healthcare services codings to determine the type of appointment inside the model itself.

  get isDayCenter(): boolean {
    return !!this.healthcareServices?.[0]?.display
      ?.toLowerCase()
      ?.includes('day center')
  }

  get isSpecialistVisit(): boolean {
    return !!this.healthcareServices?.[0]?.display
      ?.toLowerCase()
      ?.includes('clinical specialist')
  }

  get isDelivery(): boolean {
    return !!this.healthcareServices?.[0]?.display
      ?.toLowerCase()
      ?.includes('deliver')
  }

  get isInitialAssessment(): boolean {
    const serviceDisplay = this.healthcareServices?.[0]?.display?.toLowerCase()
    return (
      !!serviceDisplay &&
      serviceDisplay.includes('initial') &&
      serviceDisplay.includes('assessment')
    )
  }

  get comment(): string | undefined {
    return this.resource.comment
  }

  set comment(value: string | undefined) {
    this.resource.comment = value
  }

  getDisplay(): string {
    const [healthcareService] = this.healthcareServices
    return this.title ?? healthcareService?.display ?? 'Untitled Appointment'
  }

  @TagDecorator({
    system: codeSystems.APPOINTMENT_RECURRENCE,
  })
  recurrence?: Coding

  @TagDecorator({
    system: APPOINTMENT_RECURRENCE_INSTANCE_LINK,
  })
  recurrenceInstanceLink?: Coding

  get recurrenceInstanceLinkDisplay(): string | undefined {
    return this.recurrenceInstanceLink?.display?.split('|')[0]
  }

  // An ID for a group of recurring appointments (i.e. booked at the same time)
  get recurrenceGroupId(): string | undefined {
    return this.recurrenceInstanceLink?.code ?? this.recurrence?.code
  }

  @Extension({
    url: APPOINTMENT_NOTES,
    kind: 'valueString',
  })
  notes?: string

  @TagDecorator({
    system: codeSystems.REQUIRES_TRANSPORTATION,
  })
  requiresTransportation?: CodeableConcept

  get requiresTransportationBoolean(): boolean | undefined {
    return (
      !!this.requiresTransportation &&
      codeFromCodeableConcept(this.requiresTransportation) !== 'false'
    )
  }

  set requiresTransportationBoolean(value: boolean | undefined) {
    this.requiresTransportation = value
      ? { coding: [{ code: value ? 'true' : 'false' }] }
      : undefined
  }

  get gcalEventId(): string | undefined {
    return this.identifier(codeSystems.GCAL_EVENT_SYSTEM_URL)?.value
  }

  set gcalEventId(value: string | undefined) {
    if (value) {
      this.addIdentifier(codeSystems.GCAL_EVENT_SYSTEM_URL, value)
    } else {
      this.removeIdentifier(codeSystems.GCAL_EVENT_SYSTEM_URL)
    }
  }

  createGcalEventId(): string {
    // https://developers.google.com/workspace/calendar/api/v3/reference/events/insert#:~:text=writable-,id,writable,-location
    return base32hexEncode(this.id, { pad: false }).toLowerCase()
  }

  @Extension({
    url: codeSystems.APPOINTMENT_TITLE,
    kind: 'valueString',
  })
  _title?: string

  get title(): string | undefined {
    return (
      this._title ??
      this.healthcareServices?.[0]?.display ??
      this.practitionerBlockDisplay
    )
  }

  set title(value: string | undefined) {
    this._title = value
  }

  get titleParticipantAndService(): string | undefined {
    if (this.practitionerBlockDisplay) {
      return this.practitionerBlockDisplay
    }
    let title = ''
    if (this.patient) {
      title = `${this.patient.display}`
      if (this.healthcareServices.length) {
        title += ` ${this.healthcareServices.map((h) => h?.display).join(', ')}`
      }
    }
    return title || 'Appointment'
  }

  @Extension({
    url: `${codeSystems.SEEN_HEALTH_SYSTEM}/StructureDefinition/appointment-checkin-reminder-dismissed`,
    kind: 'valueBoolean',
  })
  appointmentCheckinReminderDismissed?: boolean

  @Extension({
    url: codeSystems.APPOINTMENT_CONFERENCE_ID,
    kind: 'valueString',
  })
  externalCalendarConferenceId?: string

  @Extension({
    url: codeSystems.APPOINTMENT_CONFERENCE_NOTES,
    kind: 'valueString',
  })
  externalCalendarConferenceNotes?: string

  @Extension({
    url: codeSystems.APPOINTMENT_LOCATION,
    kind: 'valueString',
  })
  externalCalendarLocation?: string

  @TagDecorator({
    system: codeSystems.APPOINTMENT_ALL_DAY,
  })
  allDay?: CodeableConcept

  get allDayBoolean(): boolean {
    return !!this.allDay && codeFromCodeableConcept(this.allDay) !== 'false'
  }

  get isOriginGCal(): boolean {
    return this.origin === codeSystems.AppointmentOrigin.GCal
  }

  // Google event kind for synthesized gcal appointments ('outOfOffice',
  // 'focusTime', …); undefined for Seen appointments and untyped gcal events.
  get gcalEventType(): string | undefined {
    return this.getTag(codeSystems.APPOINTMENT_GCAL_EVENT_TYPE)?.code
  }

  get isGCalOutOfOffice(): boolean {
    return this.gcalEventType === codeSystems.GCAL_EVENT_TYPE_OUT_OF_OFFICE
  }

  // The calendar owner's RSVP on a synthesized gcal event. Each gcal
  // appointment is built per-practitioner and getPractitionerCalendarEvents
  // puts the owner first in participant[] (other attendees follow as
  // Practitioner?email= search references). Undefined off the gcal origin —
  // Seen appointments use participant status for a different workflow.
  get gcalResponseStatus(): AppointmentParticipant['status'] | undefined {
    if (!this.isOriginGCal) {
      return undefined
    }
    return this.resource.participant?.[0]?.status
  }

  get origin(): AppointmentOrigin | undefined {
    return this.getTag(codeSystems.APPOINTMENT_ORIGIN)?.code as
      | AppointmentOrigin
      | undefined
  }

  set origin(value: AppointmentOrigin) {
    this.setTag(codeSystems.APPOINTMENT_ORIGIN, value)
  }

  get scheduleProposalBatchId(): string | undefined {
    return this.getTag(codeSystems.SCHEDULE_PROPOSAL_SYSTEM)?.code
  }

  set scheduleProposalBatchId(value: string) {
    this.setTag(codeSystems.SCHEDULE_PROPOSAL_SYSTEM, value)
  }

  get bookedFromSlotRecommendation(): boolean {
    return (
      this.getTag(codeSystems.APPOINTMENT_SLOT_RECOMMENDATION_SYSTEM)?.code ===
      codeSystems.APPOINTMENT_SLOT_RECOMMENDATION_BOOKED
    )
  }

  markBookedFromSlotRecommendation(): void {
    this.setTag(
      codeSystems.APPOINTMENT_SLOT_RECOMMENDATION_SYSTEM,
      codeSystems.APPOINTMENT_SLOT_RECOMMENDATION_BOOKED
    )
  }

  protected getExportFields() {
    return {
      id: this.id,
      start: this.start,
      end: this.end,
      status: this.status,
      reason: this.reason,
      comment: this.comment,
      isPast: this.isPast,
      isCancelled: this.isCancelled,
      isDayCenter: this.isDayCenter,
      isSpecialistVisit: this.isSpecialistVisit,
      locationId: resolveId(this.location),
      patientId: resolveId(this.patient),
      practitionerIds: this.practitioners.map((p) => resolveId(p) ?? ''),
      location: this.locationDisplay,
      healthcareServiceIds: this.healthcareServices.map(
        (h) => resolveId(h) ?? ''
      ),
      notes: this.notes,
      title: this.title,
      requiresTransportation: this.requiresTransportationBoolean,
      allDay: this.allDayBoolean,
      conferenceId: this.externalCalendarConferenceId,
      conferenceNotes: this.externalCalendarConferenceNotes,
      externalLocation: this.externalCalendarLocation,
      recurrence: this.recurrence?.code,
      isOriginGCal: this.isOriginGCal,
      basedOnId: resolveId(this.basedOn?.[0]),
      basedOnIds: this.basedOn?.map((b) => resolveId(b) ?? ''),
      isDelivery: this.isDelivery,
      pickupLocationId: resolveId(this.pickupLocation),
      pickupLocation: this.pickupLocation?.display,
      appointmentType: this.appointmentType,
      isPractitionerBlock: this.isPractitionerBlock,
    }
  }
}

export const build = ({
  practitioner,
  healthcareService,
  location,
  pickupLocation,
  patient,
  start,
  end,
  notes,
  requiresTransportation,
  blockType,
  extension: incomingExtension,
  participant: incomingParticipants,
  meta: incomingMeta,
  ...rest
}: {
  patient?: Reference<Patient>
  healthcareService?: Reference<HealthcareService>
  practitioner?: Reference<Practitioner>
  location?: Reference<Location>
  pickupLocation?: Reference<Location>
  start: string | undefined
  end: string | undefined
  notes?: string
  requiresTransportation?: boolean
  participant?: AppointmentParticipant[]
  blockType?: codeSystems.PractitionerBlockType
} & Omit<
  Appointment,
  'resourceType' | 'participant' | 'appointmentType'
>): ExtendedAppointment => {
  if (blockType) {
    invariant(
      practitioner,
      'Practitioner is required for practitioner-block appointments'
    )
  } else {
    invariant(patient, 'Patient is required')
    invariant(healthcareService, 'Healthcare service is required')
  }

  const participants: AppointmentParticipant[] = compact([
    patient && {
      actor: patient,
      status: 'accepted' as const,
    },
    healthcareService && {
      actor: healthcareService,
      status: 'accepted' as const,
    },
    practitioner && {
      actor: practitioner,
      status: 'accepted' as const,
    },
  ])
  if (location) {
    participants.push({
      actor: location,
      status: 'accepted',
    })
  }
  if (pickupLocation) {
    participants.push({
      actor: pickupLocation,
      type: [
        {
          coding: [
            {
              system: codeSystems.DELIVERY_LOCATION_TYPE,
              code: codeSystems.DeliveryLocationType.Pickup,
              display: 'Pickup Location',
            },
          ],
        },
      ],
      status: 'accepted',
    })
  }
  if (incomingParticipants?.length) {
    participants.push(...incomingParticipants)
  }

  const uniqParticipants = uniqBy(participants, (v) =>
    v.actor ? getReferenceString(v.actor) : JSON.stringify(v)
  )

  const extension: FHIRExtension[] = []
  if (notes) {
    extension.push({
      url: APPOINTMENT_NOTES,
      valueString: notes,
    })
  }

  const tag: Coding[] = [
    {
      system: codeSystems.APPOINTMENT_ORIGIN,
      code: codeSystems.AppointmentOrigin.Seen,
    },
  ]
  if (requiresTransportation) {
    tag.push({
      system: codeSystems.REQUIRES_TRANSPORTATION,
    })
  }

  const [uniqExtension] = merge({
    current: extension,
    incoming: incomingExtension,
  })
  invariant(uniqExtension, 'Extension must be defined after merge')

  const [meta] = merge({
    current: {
      tag,
    },
    incoming: incomingMeta,
  })
  invariant(meta, 'Meta must be defined after merge')

  return new ExtendedAppointment({
    ...rest,
    start,
    end,
    appointmentType: blockType
      ? codeableConcept({
          system: codeSystems.APPOINTMENT_TYPE,
          code: blockType,
          display: codeSystems.PRACTITIONER_BLOCKS[blockType].display,
        })
      : undefined,
    participant: uniqParticipants,
    extension: uniqExtension,
    meta,
  })
}

export const appointmentStatusDisplay: Record<Appointment['status'], string> = {
  proposed: 'Proposed',
  pending: 'Pending',
  booked: 'Booked',
  arrived: 'Arrived',
  fulfilled: 'Fulfilled',
  cancelled: 'Cancelled',
  noshow: 'No Show',
  waitlist: 'Waitlist',
  'entered-in-error': 'Entered in Error',
  'checked-in': 'Checked In',
}
